import { create } from 'zustand'
import { fetchUserAttributes, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'

interface UserState {
  username: string
  email: string
  name: string
  groups: string[]
  isAdmin: boolean
  isLoading: boolean
  error: string | null
  fetchUserInfo: () => Promise<void>
}

export const useUserStore = create<UserState>((set) => ({
  username: '',
  email: '',
  name: '',
  groups: [],
  isAdmin: false,
  isLoading: false,
  error: null,

  fetchUserInfo: async () => {
    set({ isLoading: true, error: null })
    
    try {
      const { username } = await getCurrentUser()
      
      // Get session tokens first — works reliably for both SSO and password users
      const { tokens } = await fetchAuthSession()
      const payload = tokens?.accessToken?.payload
      const idPayload = tokens?.idToken?.payload
      
      // fetchUserAttributes can fail for federated/SSO users — don't let it break everything
      let attributes: Partial<Record<string, string>> = {}
      try {
        attributes = await fetchUserAttributes()
      } catch (e) {
        console.warn('fetchUserAttributes failed (expected for SSO users):', e)
      }
      
      // Extract groups from access token
      let userGroups: string[] = []
      const cognitoGroups = payload?.['cognito:groups']
      if (cognitoGroups) {
        userGroups = Array.isArray(cognitoGroups) 
          ? cognitoGroups.map(g => String(g)) 
          : [String(cognitoGroups)]
      }
      
      const isAdmin = userGroups.includes('sec-website-admin')
      
      // Resolve display name: try attributes first, then ID token claims, then username
      const displayName = attributes.given_name 
        || attributes.name 
        || (idPayload?.['given_name'] as string)
        || (idPayload?.['name'] as string)
        || username
      
      const email = attributes.email 
        || (idPayload?.['email'] as string) 
        || ''

      console.log('User info resolved:', { username, displayName, email, groups: userGroups, isAdmin })
      
      set({
        username,
        email,
        name: displayName,
        groups: userGroups,
        isAdmin,
        isLoading: false
      })
    } catch (error) {
      console.error('Error fetching user info:', error)
      set({
        username: '',
        email: '',
        name: '',
        groups: [],
        isAdmin: false,
        isLoading: false,
        error: 'Failed to fetch user information'
      })
    }
  }
}))
